import {
  BulkUpdateDetailType,
  BulkUpdateRecordType,
} from '@nxsys/types/models/bulkUpdate';
import { PaginationType } from '@nxsys/types/models/Common';
import { createSlice } from '@reduxjs/toolkit';
import { bulkUpdateActs } from '../action';
import { appIntl } from '@nxsys/helpers/Common';
import { showToast } from '@nxsys/utils/toast';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { GetEmployeeDataForBulkUpdateParams, GetSubBulkImportParams } from '../type';

export type BulkUpdateState = {
  bulkUpdateData: {
    list: BulkUpdateRecordType[];
    pagination: PaginationType;
    queryParams: GetSubBulkImportParams | null;
  };
  loading: boolean;
  importLoading: boolean;
  validateLoading: boolean;
  saveLoading: boolean;
  bulkUpdateDetailData: BulkUpdateDetailType | null;
  resendLoginInfoLoading: boolean;
  employeeData: {
    list: EmployeeRecordType[];
    pagination: PaginationType;
    queryParams: GetEmployeeDataForBulkUpdateParams;
    employeeDataLoading: boolean;
    exportLoading: boolean;
  };
};

export const initialState: BulkUpdateState = {
  bulkUpdateData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  loading: false,
  importLoading: false,
  validateLoading: false,
  saveLoading: false,
  bulkUpdateDetailData: null,
  resendLoginInfoLoading: false,
  employeeData: {
    list: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: {
      page: 0,
      size: 20,
    },
    employeeDataLoading: false,
    exportLoading: false,
  },
};

const bulkUpdateSlice = createSlice({
  name: 'bulkUpdate',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(bulkUpdateActs.getSubBulkImport.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      bulkUpdateActs.getSubBulkImport.fulfilled,
      (state, action) => {
        state.loading = false;
        state.bulkUpdateData = {
          list: action.payload.data,
          pagination: action.payload.pagination,
          queryParams: (action.payload as any).queryParams,
        };
      },
    );
    builder.addCase(
      bulkUpdateActs.getSubBulkImport.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.getSubBulkImport.error',
            }),
        });
      },
    );
    builder.addCase(bulkUpdateActs.importBulkUpdateSub.pending, (state) => {
      state.importLoading = true;
    });
    builder.addCase(bulkUpdateActs.importBulkUpdateSub.fulfilled, (state) => {
      state.importLoading = false;
    });
    builder.addCase(
      bulkUpdateActs.importBulkUpdateSub.rejected,
      (state, action) => {
        state.importLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.importBulkUpdateSub.error',
            }),
        });
      },
    );
    builder.addCase(
      bulkUpdateActs.validateImportBulkUpdateSub.pending,
      (state) => {
        state.validateLoading = true;
      },
    );
    builder.addCase(
      bulkUpdateActs.validateImportBulkUpdateSub.fulfilled,
      (state) => {
        state.validateLoading = false;
      },
    );
    builder.addCase(
      bulkUpdateActs.validateImportBulkUpdateSub.rejected,
      (state, action) => {
        state.validateLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.validateImportBulkUpdateSub.error',
            }),
        });
      },
    );
    builder.addCase(bulkUpdateActs.saveImportBulkUpdateSub.pending, (state) => {
      state.saveLoading = true;
      showToast({
        type: 'info',
        message: appIntl().formatMessage({
          id: 'bulkUpdate.import.save.inProgress',
        }),
      });
    });
    builder.addCase(
      bulkUpdateActs.saveImportBulkUpdateSub.fulfilled,
      (state) => {
        state.saveLoading = false;
      },
    );
    builder.addCase(
      bulkUpdateActs.saveImportBulkUpdateSub.rejected,
      (state, action) => {
        state.saveLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.import.save.error',
            }),
        });
      },
    );
    builder.addCase(bulkUpdateActs.getBulkUpdateById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      bulkUpdateActs.getBulkUpdateById.fulfilled,
      (state, action) => {
        state.loading = false;
        state.bulkUpdateDetailData = action.payload.data;
      },
    );
    builder.addCase(
      bulkUpdateActs.getBulkUpdateById.rejected,
      (state, action) => {
        state.loading = false;
        state.bulkUpdateDetailData = null;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.getBulkUpdateById.error',
            }),
        });
      },
    );
    builder.addCase(bulkUpdateActs.resendLoginInfo.pending, (state) => {
      state.resendLoginInfoLoading = true;
    });
    builder.addCase(bulkUpdateActs.resendLoginInfo.fulfilled, (state) => {
      state.resendLoginInfoLoading = false;
    });
    builder.addCase(
      bulkUpdateActs.resendLoginInfo.rejected,
      (state, action) => {
        state.resendLoginInfoLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.resendLoginInfo.error',
            }),
        });
      },
    );
    builder.addCase(
      bulkUpdateActs.getEmployeeDataForBulkUpdate.pending,
      (state) => {
        state.employeeData.employeeDataLoading = true;
      },
    );
    builder.addCase(
      bulkUpdateActs.getEmployeeDataForBulkUpdate.fulfilled,
      (state, action) => {
        state.employeeData.employeeDataLoading = false;
        state.employeeData.list = action.payload.data;
        state.employeeData.pagination = action.payload.pagination;
        state.employeeData.queryParams = action.payload.queryParams;
      },
    );
    builder.addCase(
      bulkUpdateActs.getEmployeeDataForBulkUpdate.rejected,
      (state, action) => {
        state.employeeData.employeeDataLoading = false;
        state.employeeData = {
          list: [],
          pagination: {
            current: 0,
            pageSize: 20,
            totalDocs: 0,
          },
          queryParams: {
            page: 0,
            size: 20,
          },
          employeeDataLoading: false,
          exportLoading: false,
        };
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.getEmployeeDataForBulkUpdate.error',
            }),
        });
      },
    );
    builder.addCase(
      bulkUpdateActs.exportEmployeeDataForBulkUpdate.pending,
      (state) => {
        state.employeeData.exportLoading = true;
      },
    );
    builder.addCase(
      bulkUpdateActs.exportEmployeeDataForBulkUpdate.fulfilled,
      (state) => {
        state.employeeData.exportLoading = false;
      },
    );
    builder.addCase(
      bulkUpdateActs.exportEmployeeDataForBulkUpdate.rejected,
      (state, action) => {
        state.employeeData.exportLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.exportEmployeeDataForBulkUpdate.error',
            }),
        });
      },
    );
    builder.addCase(
      bulkUpdateActs.getEmployeeDataByImportId.pending,
      (state) => {
        state.importLoading = true;
      },
    );
    builder.addCase(
      bulkUpdateActs.getEmployeeDataByImportId.fulfilled,
      (state) => {
        state.importLoading = false;
      },
    );
    builder.addCase(
      bulkUpdateActs.getEmployeeDataByImportId.rejected,
      (state, action) => {
        state.importLoading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'bulkUpdate.getEmployeeDataByImportId.error',
            }),
        });
      },
    );
  },
});

export const bulkUpdateReducer = bulkUpdateSlice.reducer;
export const bulkUpdateActions = {
  ...bulkUpdateSlice.actions,
  ...bulkUpdateActs,
};
